import React, { useMemo, useState, useCallback } from 'react'
import styles from '../offers/manage/OfferManagementDetail.module.scss'
import { formatAmount } from '../../utils/number'
import { getYYYYMMString } from '../../utils/date'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../utils/incentiveDisplay'

const CONTRACT_FIELD_CONFIGS = {
	termContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'salaryAmountForPeriod',
			label: '기간 급여액',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursement',
			label: '해외대회 지원금',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)}%`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
	domesticContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'weeklySalaryCutThrough',
			label: '컷통과',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'weeklySalaryCutFail',
			label: '컷탈락',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursementJeju',
			label: '제주 대회 지원금',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)} 원`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
	overseasContract: [
		{
			key: 'isSalaryTaxIncluded',
			label: '세금 적용 여부',
			formatter: (value) => (value ? '세후' : '세전'),
		},
		{
			key: 'payrollCycles',
			label: '임금 지급 주기',
			formatter: (value) => (value === 'weekly' ? '주급' : '일시불제'),
		},
		{
			key: 'weeklySalaryCutThrough',
			label: '컷통과',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'weeklySalaryCutFail',
			label: '컷탈락',
			formatter: (value) => `${formatAmount(value)} 원`,
		},
		{
			key: 'prizeType',
			label: INCENTIVE_BASIS_LABEL,
			formatter: (value) => formatPrizeTypeLabel(value),
		},
		{
			key: 'incentives',
			label: '인센티브 금액',
			type: 'incentive',
		},
		{
			key: 'expenseReimbursement',
			label: '해외 대회 지원금',
			formatter: (value) => (value === 0 ? '미지원' : `${formatAmount(value)} %`),
		},
		{
			key: 'wearSponsoredHat',
			label: '스폰서 모자 착용',
			formatter: (value) => (value ? '착용' : '미착용'),
		},
	],
}

const CONTRACT_TYPE_LABELS = {
	single: '단일계약',
	long_term: '장기계약',
	year_first_half: '상반기 연봉계약',
	year_second_half: '하반기 연봉계약',
	both: '상·하반기 연봉계약',
}

function sortJobPostTours(tours) {
	if (!tours?.length) return []
	return [...tours].sort((a, b) => {
		const startA = new Date(a.startedAt)
		const startB = new Date(b.startedAt)
		if (startA.getTime() !== startB.getTime()) return startA - startB
		return new Date(a.endedAt) - new Date(b.endedAt)
	})
}

function sortIncentiveSettings(incentiveSettings) {
	if (!incentiveSettings?.length) return []
	return [...incentiveSettings].sort((a, b) => (a.startRank || 999) - (b.startRank || 999))
}

const JobPostPublicContractSection = React.memo(function JobPostPublicContractSection({ detail }) {
	const [domesticOverseasSwapContent, setDomesticOverseasSwapContent] = useState(true)

	const overseasTourIncluded = useMemo(() => {
		if (detail?.termContract) {
			const { isOverseasIncluded, expenseReimbursement } = detail.termContract
			if (isOverseasIncluded) return true
			if (
				typeof expenseReimbursement === 'number' &&
				!Number.isNaN(expenseReimbursement) &&
				expenseReimbursement > 0
			) {
				return true
			}
		}
		return (detail?.tours || []).some(
			(t) => t.region === 'INTERNATIONAL' || t.region === 'BOTH',
		)
	}, [detail])

	const tourCategories = useMemo(() => {
		const types = [...new Set((detail?.tours || []).map((t) => t.type).filter(Boolean))]
		if (types.length) return types.join(', ')
		const r = detail?.region
		if (r === 'DOMESTIC') return '국내'
		if (r === 'INTERNATIONAL') return '해외'
		if (r === 'BOTH') return '국내·해외'
		return '미정'
	}, [detail])

	const contractTypeLabel = useMemo(() => {
		const ct = detail?.contractType
		return (ct && CONTRACT_TYPE_LABELS[ct]) || '미정'
	}, [detail?.contractType])

	const periodContractText = useMemo(() => {
		const ct = detail?.contractType
		if (ct === 'both') return '상반기 / 하반기'
		if (ct === 'year_first_half') return '상반기'
		if (ct === 'year_second_half') return '하반기'
		if (detail?.termContract && detail.contractStartedAt && detail.contractEndedAt) {
			return `${detail.contractStartedAt} ~ ${detail.contractEndedAt}`
		}
		const tours = detail?.tours || []
		if (tours.length === 0) return '기간 미정'
		const sorted = sortJobPostTours(tours)
		const start = sorted[0].startedAt
		const end = sorted.reduce(
			(latest, t) => (new Date(t.endedAt) > new Date(latest) ? t.endedAt : latest),
			sorted[0].endedAt,
		)
		return `${getYYYYMMString(start).slice(0, 8)} ~ ${getYYYYMMString(end).slice(0, 8)}`
	}, [detail])

	const sortedTours = useMemo(() => sortJobPostTours(detail?.tours), [detail?.tours])

	const renderContractField = useCallback((config, contractData) => {
		const value = contractData?.[config.key]
		return (
			<div key={config.key} className={styles.fieldContainer}>
				<div className={styles.fieldLabel}>{config.label}</div>
				<div className={styles.fieldContent}>
					{config.formatter ? config.formatter(value) : value}
				</div>
			</div>
		)
	}, [])

	const renderIncentiveField = useCallback((config, incentives) => {
		const sortedIncentives = sortIncentiveSettings(incentives)
		return (
			<div key={config.key} className={styles.fieldContainer}>
				<div className={styles.fieldLabel}>{config.label}</div>
				<div className={styles.fieldContent}>
					{sortedIncentives.length > 0 ? (
						<div className={styles.incentiveList}>
							{sortedIncentives.map((setting, mapIndex) => {
								if (setting.percentage === null && setting.endRank === null)
									return null
								const displayRange = (() => {
									if (mapIndex === 0 || setting.startRank === 1) return '우승'
									if (setting.startRank === null && setting.endRank === null)
										return '순위 미정'
									if (setting.endRank === 0) return `${setting.startRank}위 ~ CUT`
									if (
										setting.startRank !== null &&
										setting.endRank !== null &&
										setting.startRank !== setting.endRank
									) {
										return `${setting.startRank}위 ~ ${setting.endRank}위`
									}
									if (setting.startRank !== null && setting.endRank === null)
										return `${setting.startRank}위 ~`
									if (setting.startRank !== null) return `${setting.startRank}위`
									return '순위 미정'
								})()
								const displayPercentage =
									setting.percentage !== null
										? `${setting.percentage}%`
										: '미입력'
								return (
									<div key={mapIndex} className={styles.incentiveDisplayItem}>
										<span className={styles.incentiveRank}>{displayRange}</span>
										<span className={styles.incentiveSeparator}>:</span>
										<span className={styles.incentiveValue}>
											{displayPercentage}
										</span>
									</div>
								)
							})}
						</div>
					) : (
						<div className={styles.incentiveDisplayItem}>
							<div>인센티브 정보 없음</div>
						</div>
					)}
				</div>
			</div>
		)
	}, [])

	const renderContractFields = useCallback(
		(contractType, contractData) => {
			const fields = CONTRACT_FIELD_CONFIGS[contractType] || []
			return fields.map((field) => {
				if (field.type === 'incentive') {
					return renderIncentiveField(field, contractData?.[field.key])
				}
				return renderContractField(field, contractData)
			})
		},
		[renderContractField, renderIncentiveField],
	)

	const hasAnyContract =
		Boolean(detail?.termContract) ||
		Boolean(detail?.domesticContract) ||
		Boolean(detail?.overseasContract)

	if (!detail) return null

	return (
		<>
			<div className={styles.basicContractInfoHeader}>기본 계약정보</div>
			<div className={styles.basicContractInfoTourType}>
				<div className={styles.basicLabel}>투어종류</div>
				<div className={styles.basicContent}>{tourCategories}</div>
			</div>
			<div className={styles.basicContractInfoContractType}>
				<div className={styles.basicLabel}>계약유형</div>
				<div className={styles.basicContent}>{contractTypeLabel}</div>
			</div>
			<div className={styles.periodContainer}>
				<div className={styles.periodLabel}>계약기간</div>
				<div className={styles.periodContent}>{periodContractText}</div>
			</div>
			<div className={styles.tourListContainer}>
				<div className={styles.tourListLabel}>대회목록</div>
				<div className={styles.tourListContent}>
					{sortedTours.length === 0 && '미정'}
					{sortedTours.length > 0 &&
						sortedTours.map((tour, index) => (
							<div key={tour.id || index} className={styles.tourListItemContainer}>
								<div className={styles.tourListItemIndex}>({index + 1})</div>
								<div className={styles.tourListItemMain}>
									<div className={styles.tourName}>{tour.name}</div>
									<div className={styles.tourDate}>
										{getYYYYMMString(tour.startedAt).slice(0, 8)} ~{' '}
										{getYYYYMMString(tour.endedAt).slice(0, 8)}
									</div>
								</div>
							</div>
						))}
				</div>
			</div>
			<div className={styles.isOverSeasTourIncludeContainer}>
				<div className={styles.isOverSeasTourIncludeLabel}>해외 대회 포함 여부</div>
				<div className={styles.isOverSeasTourIncludeContent}>
					{overseasTourIncluded ? '해외대회 포함' : '해외대회 미포함'}
				</div>
			</div>

			{hasAnyContract ? (
				<>
					<div className={styles.detailContractHeader}>상세 계약정보</div>
					{detail.termContract !== null && detail.termContract !== undefined && (
						<>{renderContractFields('termContract', detail.termContract)}</>
					)}
					{detail.domesticContract && detail.overseasContract && (
						<>
							<div className={styles.domesticOverseasSwapContainer}>
								<button
									type="button"
									className={
										domesticOverseasSwapContent
											? styles.seeDomesticChosen
											: styles.seeDomesticUnchosen
									}
									onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
									국내
								</button>
								<button
									type="button"
									className={
										domesticOverseasSwapContent
											? styles.seeOverseasUnchosen
											: styles.seeOverseasChosen
									}
									onClick={() => setDomesticOverseasSwapContent((prev) => !prev)}>
									해외
								</button>
							</div>
							{domesticOverseasSwapContent &&
								renderContractFields('domesticContract', detail.domesticContract)}
							{!domesticOverseasSwapContent &&
								renderContractFields('overseasContract', detail.overseasContract)}
						</>
					)}
					{detail.overseasContract && !detail.domesticContract && (
						<>{renderContractFields('overseasContract', detail.overseasContract)}</>
					)}
					{detail.domesticContract && !detail.overseasContract && (
						<>{renderContractFields('domesticContract', detail.domesticContract)}</>
					)}
				</>
			) : null}
		</>
	)
})

export default JobPostPublicContractSection
